/**
 * Proyecta un punto sobre una polilínea para calcular distancia restante.
 */

const R = 6371; // Radio Tierra km

function toRad(d: number) { return d * Math.PI / 180; }

function haversine(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export interface PolylineProjection {
  nearestIndex: number;      // Índice del segmento más cercano
  nearestLat: number;        // Punto proyectado sobre la polilínea
  nearestLon: number;
  distanceToRoute: number;   // Distancia perpendicular en km
  remainingKm: number;       // Distancia restante hasta el final
  progressPercent: number;   // 0-100
  totalKm: number;           // Distancia total de la polilínea
}

export function projectOnPolyline(
  lat: number,
  lon: number,
  polyline: [number, number][]
): PolylineProjection | null {
  if (!polyline || polyline.length < 2) return null;

  let minDist = Infinity;
  let bestIdx = 0;
  let bestT = 0;

  // Encontrar el segmento más cercano
  for (let i = 0; i < polyline.length - 1; i++) {
    const [aLat, aLon] = polyline[i];
    const [bLat, bLon] = polyline[i + 1];

    // Proyección paramétrica (coordenadas planas, suficientemente preciso para distancias cortas)
    const dx = bLon - aLon;
    const dy = bLat - aLat;
    const lenSq = dx * dx + dy * dy;

    let t = 0;
    if (lenSq > 1e-12) {
      t = Math.max(0, Math.min(1, ((lon - aLon) * dx + (lat - aLat) * dy) / lenSq));
    }

    const projLat = aLat + t * dy;
    const projLon = aLon + t * dx;
    const d = haversine(lat, lon, projLat, projLon);

    if (d < minDist) {
      minDist = d;
      bestIdx = i;
      bestT = t;
    }
  }

  // Calcular distancias acumuladas
  const segDists: number[] = [];
  let totalKm = 0;
  for (let i = 0; i < polyline.length - 1; i++) {
    const d = haversine(polyline[i][0], polyline[i][1], polyline[i + 1][0], polyline[i + 1][1]);
    segDists.push(d);
    totalKm += d;
  }

  // Distancia recorrida hasta el punto proyectado
  let traveled = 0;
  for (let i = 0; i < bestIdx; i++) traveled += segDists[i];
  traveled += segDists[bestIdx] * bestT;

  const remaining = totalKm - traveled;

  const [aLat, aLon] = polyline[bestIdx];
  const [bLat, bLon] = polyline[bestIdx + 1];
  const nearestLat = aLat + bestT * (bLat - aLat);
  const nearestLon = aLon + bestT * (bLon - aLon);

  return {
    nearestIndex: bestIdx,
    nearestLat,
    nearestLon,
    distanceToRoute: minDist,
    remainingKm: Math.max(0, remaining),
    progressPercent: totalKm > 0 ? Math.min(100, (traveled / totalKm) * 100) : 0,
    totalKm,
  };
}
